/**
 * @graphql cc
 */
import { useEffect, useRef } from 'react';

import type { InlineCommentPluginState } from '@atlaskit/editor-plugin-annotation/pm-plugins/types';

import {
	handleDeepLinkNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { fg } from '@confluence/feature-gating';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	useCommentsPanelActions,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';

type FocusedCommentProps = {
	commentId: string | undefined;
	contentId: string;
	annotationState: InlineCommentPluginState | undefined;
	handleGqlError: (error: Error) => void;
};

// Using common utility function from comments-util

export function useFocusedComment({
	commentId,
	contentId,
	annotationState,
	handleGqlError,
}: FocusedCommentProps): void {
	const commentDataResult = useCommentById({
		commentId: commentId || '',
		contentId,
		options: {
			skip: !commentId,
			onError: handleGqlError,
		},
	});

	const { setCurrentlySelectedThreadKey, setCommentContainerToFocus } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();

	const hasOpenedFocusedComment = useRef(false);

	// Reset hasOpenedFocusedComment when commentId changes
	useEffect(() => {
		if (hasOpenedFocusedComment.current) {
			hasOpenedFocusedComment.current = false;
		}
	}, [commentId]);

	useEffect(() => {
		if (hasOpenedFocusedComment.current) {
			return;
		}
		const focusedComment = commentDataResult.comment ?? null;

		if (commentId && focusedComment) {
			const markerRef = focusedComment.location?.inlineMarkerRef ?? '';
			const isResolved = Boolean(focusedComment.location?.inlineResolveProperties?.resolved);

			if (isResolved) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });

				if (markerRef) {
					setCurrentlySelectedThreadKey(markerRef);
					setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
					hasOpenedFocusedComment.current = true;
				}
			} else if (
				// dangling comment
				markerRef &&
				annotationState?.annotationsLoaded &&
				// On live docs annotationsLoaded can flip to true before the collab
				// provider has synced the document, so `annotations` is briefly {}
				// and a valid markerRef gets misclassified as dangling. Requiring
				// at least one annotation closes that race; truly dangling cases
				// always have other annotations present in the document. Gated for
				// safe rollout — gate OFF preserves the prior (buggy) behavior.
				// See bug 19 in https://hello.atlassian.net/wiki/spaces/SCE/pages/7070032438
				(!fg('confluence_focused_comment_empty_annotation_check') ||
					Object.keys(annotationState.annotations).length > 0) &&
				!(markerRef in annotationState.annotations) &&
				fg('confluence_frontend_new_dangling_comments_ux')
			) {
				setCurrentlySelectedThreadKey(markerRef);
				setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
				showCommentsPanel({ openWithView: ViewValues.DELETED_CONTENT });
				hasOpenedFocusedComment.current = true; // to prevent focused comment from being opened again. We may need a better way to handle this
			} else {
				const commentElement = document.getElementById(markerRef);

				if (commentElement && markerRef !== '') {
					// Use unified navigation for deep linking
					const success = handleDeepLinkNavigation({
						scrollBehavior: ScrollBehavior.INSTANT,
						context: NavigationContext.EDITOR,
						block: 'center',
						inline: 'nearest',
						markElement: commentElement,
						commentId,
					});

					if (success) {
						hasOpenedFocusedComment.current = true;
					}
				}
			}
		}
		return () => {
			hasOpenedFocusedComment.current = false;
		};
	}, [
		commentId,
		commentDataResult.comment,
		annotationState?.annotations,
		annotationState?.annotationsLoaded,
		showCommentsPanel,
		setCurrentlySelectedThreadKey,
		setCommentContainerToFocus,
	]);
}
